<template>
    <div class="category-component">
        <card title="Category Information">
            <template #header-right>
                <history-modal v-model="category" />
            </template>
            <form>
                <b-row v-show="showForm">
                    <b-col>
                        <fg-input id="name" v-model="category.name" label="Category Name">
                        </fg-input>
                    </b-col>
                </b-row>
                <b-row v-show="!showForm">
                    <b-col>
                        <SearchComponent :id="category && +category.id" v-model="category"
                            :search-u-r-l="`/DrugCategory`" @input='fetchCategory' />
                    </b-col>
                </b-row>
                <b-row v-show="showForm">
                    <b-col>
                        <label for="notesField">Notes</label>
                        <AutoCompleteTextarea id="notesField" v-model="category.notes" class="textarea-autosize" />
                    </b-col>
                </b-row>
                <b-row v-show="showForm">
                    <b-col>
                        <SearchComponent :id="drug && drug.id" v-model="drug" :label="`Drug`" :search-u-r-l="`/Drug`" />
                    </b-col>
                    <b-col>
                        <n-button type="success" @click="addDrug">Add</n-button>
                    </b-col>
                </b-row>
                <b-row v-show="showForm" class="pt-3">
                    <b-col>
                        <b-table striped outlined :items="drugsInCategory" :fields="drugFields" sort-icon-left
                            selectable selected-variant="danger" responsive="true"
                            @row-selected="onRowSelected"></b-table>
                    </b-col>
                </b-row>
                <b-row v-show="selected.length">
                    <b-col>
                        <b-button variant="danger" :block="true" @click="removeSelectedDrugs">
                            Remove Drugs
                        </b-button>
                    </b-col>
                </b-row>
                <hr>

                <!-- Custom Questions-->
                <b-row v-if="showForm">
                    <b-col cols="12">
                        <h4>Custom Questions Configuration</h4>
                    </b-col>
                    <b-col cols="12">
                        <fg-input id="questionaryTitle" v-model="questionary.title" label="Title"></fg-input>
                    </b-col>
                    <b-col v-if="questions && questions.length" cols="12">
                        <b-row v-for="q in questions" :key="q.id">
                            <b-col cols="1">
                                <b-icon v-b-tooltip.hover title="Remove" icon="x-circle-fill" variant="danger"
                                    style="cursor: pointer" @click="removeQuestion(q)"></b-icon>
                                <b> # {{ q.indexNumber }}</b>
                            </b-col>
                            <b-col cols="8">
                                <fg-input v-model="q.questionText" placeholder='Question' label="Question"></fg-input>
                            </b-col>
                            <b-col cols="3">
                                <fg-input v-model="q.ufi" placeholder='Unique Field Identifier' label="UFI"></fg-input>
                            </b-col>
                        </b-row>
                    </b-col>
                    <b-col>
                        <b-button variant="info" @click="addNewQuestion">
                            Add New Question
                        </b-button>
                    </b-col>
                </b-row>

                <b-row>
                    <b-col>
                        <NewClearSaveButtons :titles="buttonTitles" :disable-new="showForm" :disable-save="!showForm"
                            :disable-clear="!showForm" :show-clear-modal="true" @new-clicked="createCategory"
                            @clear-clicked="undoChanges" @save-clicked="saveCategory" />
                    </b-col>
                </b-row>
            </form>
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Mixins } from 'vue-property-decorator';
import axios from 'axios';
import { Lock, LockHandler } from '@/mixins/LockHandler';
import DisableAutocompleteMixin from '@/mixins/DisableAutocompleteMixin';
import { NotificationOptions } from "@/util/NotificationOptionsPresets"

import NewClearSaveButtons from './NewClearSaveButtons.vue';
import SearchComponent from '@/components/SearchComponent.vue';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';

import { DrugCategory } from '@/models/DrugCategory';
import { Questionary } from '@/models/Questionary';
import { Drug } from '@/models/Drug/Drug';
import { Identifier } from '@/models/GSDD';
import { NewClearSaveTitles } from "@/components/NewClearSaveButtons.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { EntityType } from '@/models/EntityType';

@Component({
    name: "CategoryComponent",
    components: { HistoryModal, SearchComponent, NewClearSaveButtons, AutoCompleteTextarea },
})
export default class CategoryComponent extends Mixins(LockHandler, DisableAutocompleteMixin) {
    private drug: Drug = new Drug();
    private questionary: Questionary = new Questionary();
    private questions: Array<Questionary.Question> = [];

    private category: DrugCategory = new DrugCategory();
    private categoryLock: Lock | null = null;

    private drugsInCategory: any[] = [];
    protected drugFields = [
        { key: 'productNameLong', label: 'Name', sortable: true },
        { key: 'productIdentifier', label: 'NDC', sortable: true, formatter: 'ndcFormat' },
    ];

    private createMode = false;
    private questionsWereLoaded = false;

    protected buttonTitles = {
        save: "Save Changes",
        new: "New Category",
        clear: "Clear",
    } as NewClearSaveTitles;

    get showForm(): boolean {
        return !!(this.category?.id || this.createMode);
    }
    ndcFormat(value: Identifier[]) {
        const ndcIdentifier = value.find(p => p.identifierType == 0);
        return ndcIdentifier ? ndcIdentifier.identifier : 'Unknown';

    }
    private selected: any[] = [];
    onRowSelected(items: any[]) {
        this.selected = items;
    }

    removeSelectedDrugs() {
        for (const item of this.selected) {
            console.log("item: ", item);
            const identifiers = item.productIdentifier as Identifier[];
            const productIdent = identifiers.find(p => p.identifierType == 1);
            if (productIdent) this.removeDrug(productIdent.identifier, 0);
        }
    }

    removeDrug(productId: string, drugSource: number) {
        if (!this.category) return;
        axios.post(`/DrugCategory/${this.category.id}/Remove/${productId}/${drugSource}`)
            .then(_response => {
                this.fetchDrugs();
            })
            .catch(error => {
                console.error('Error while removing drug', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            })
    }

    fetchDrugs() {
        if (!this.category?.id) return;

        axios.get(`/DrugCategory/${this.category.id}/Drugs`)
            .then(response => {
                this.drugsInCategory = response.data;
                this.drugsInCategory.forEach(dr => {
                    dr.productIdentifier = dr.productIdentifier[1]?.identifier;
                })
            })
            .catch(error => {
                console.error('Error while getting associted drugs', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    fetchCustomQuestions() {
        if (!this.category?.id) return;

        axios.get<Questionary>(`/DrugCategory/${this.category.id}/custom-questions`)
            .then(response => {
                this.questionary = Object.assign(new Questionary(), response.data);
                this.questions = this.questionary.orderedQuestionsAsc;
                this.questionsWereLoaded = this.questions.length > 0;
            })
            .catch(error => {
                console.error('Error while getting custom questionary', { error, response: error?.response });
                this.$notification(NotificationOptions.error(error));
            });
    }

    protected categoryId: number | null = null;

    fetchCategory(category: DrugCategory) {
        console.log(category);
        if (category?.id === this.categoryId) return;
        this.categoryId = category.id as number;

        this.addLockWithURL(`/DrugCategory/${category.id}/lock`, 60000)
            .then(result => {
                this.categoryLock = result;
            })
            .catch(error => {
                if (error.response && error.response.status == 418) {
                    // already locked
                    this.showLockedMessage(
                        EntityType.DrugCategory,
                        this.category?.id.toString(),
                        category?.name,
                        error.response.data
                    );
                }
            })
            .finally(() => {
                axios.get(`/DrugCategory/${category.id}`)
                    .then(response => {
                        this.category = response.data;
                        this.fetchDrugs();
                        this.fetchCustomQuestions();
                    })
                    .catch(error => {
                        console.error('Error while getting the drug category', { error, response: error?.response });
                        this.$notification(NotificationOptions.error(error));
                    })
            });
    }

    createCategory() {
        this.createMode = true;
        this.category = new DrugCategory();
    }

    clearForm() {
        if (this.categoryLock && this.categoryLock.refreshURL) {
            this.releaseLockAtURL(this.categoryLock.refreshURL);
            this.categoryLock = null;
        }
        this.createMode = false;
        this.category = new DrugCategory();
        this.drugsInCategory = [];
        this.questionary = new Questionary();
        this.questions = [];
    }

    undoChanges() {
        this.clearForm();
    }

    saveQuestionary() {
        if (!this.questionsWereLoaded && this.questionary.amountOfQuestions == 0 || !this.category?.id) return;

        this.questionary.drugCategoryID = Number(this.category.id);
        this.questionary.prepareQuestions();
        axios.post(`/DrugCategory/custom-questions`, this.questionary)
            .then(response => {
                this.questionary = Object.assign(new Questionary(), response.data);
                this.questions = this.questionary.orderedQuestionsAsc;
                this.questionsWereLoaded = this.questions.length > 0;
                this.$notification(NotificationOptions.successSaveNotificationPreset("Custom Questions"));
            })
            .catch(error => {
                const errorMsg = 'Error while saving questions.';
                this.$notification(NotificationOptions.error(error));
                console.error(errorMsg, { error, response: error?.response })
            });
    }

    saveCategory() {
        axios.post(`/DrugCategory/`, this.category)
            .then(response => {
                this.category = response.data;
                for (let i = 0; i < this.drugsInCategory.length; i++) {
                    this.drug = this.drugsInCategory[i];
                    this.addDrug();
                }
                this.$notification(NotificationOptions.successSaveNotificationPreset("Category"));
                this.saveQuestionary();
            })
            .catch(error => {
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.createMode = false;
                //this.clearForm();
            });
    }

    addDrug() {
        if (this.category?.id) {
            if (this.drug.productID) {
                axios.post(`/DrugCategory/${this.category.id}/Add/${this.drug.productID}/${this.drug.source}`)
                    .then(_response => {
                        this.drug = new Drug();
                        this.fetchDrugs();
                    })
                    .catch(error => {
                        console.error('Error while adding drug', { error, response: error?.response });
                        this.$notification(NotificationOptions.error(error));
                    })
            }
        }
        else {
            const newDrug: any = this.drug;
            newDrug.productNameLong = this.drug.productNameShort;
            newDrug.productIdentifier = this.drug.ndc;
            this.drugsInCategory.push(newDrug);
            this.drug = new Drug();
        }

    }

    removeQuestion(q: Questionary.Question) {
        console.log('Remove Question', q);
        this.questions = this.questionary.removeQuestion(q);
    }
    addNewQuestion() {
        this.questions = this.questionary.pushNewQuestion(new Questionary.Question());
    }
}

</script>

<style scoped></style>
